import { Component, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Observable, tap } from 'rxjs';

import { ClaimOrderModel } from 'src/app/common/ClaimOrderModel';
import { ClaimOrderPositionModel } from 'src/app/common/ClaimOrderPositionModel';
import { Order } from 'src/app/common/Order';
import { OrderPositionStatusConstants } from 'src/app/common/OrderPositionStatus';
import { OrderService } from 'src/app/services/order.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { CallApiService } from '../../services/api-action.service';
import { OrderGroupOrderPositionItemComponent } from '../order-group-order-position-item/order-group-order-position-item.component';

/**
 * One seller's order within a purchase, with its positions. Read-only in the purchase list; with
 * `claimable` on the order detail page, where the buyer ticks positions and requests their
 * cancellation in one go (#199: the two were copies).
 */
@Component({
  selector: 'app-order-group-order-item',
  imports: [RouterModule, AsyncClickDirective, OrderGroupOrderPositionItemComponent],
  templateUrl: './order-group-order-item.component.html',
})
export class OrderGroupOrderItemComponent {
  private readonly orderService = inject(OrderService);
  private readonly callApiService = inject(CallApiService);

  readonly order = input<Order>();
  /** Offers the cancellation request — the detail page sets it for a reserved purchase only. */
  readonly claimable = input(false);
  /** The request went through; the page re-reads the purchase to show the positions' new status. */
  readonly claimed = output<void>();

  /** The ids of the positions ticked for the request. */
  readonly selection = new Set<string>();

  /** Positions still simply bought — once none is left, there is nothing to request. */
  get hasBoughtPositions(): boolean {
    return !!this.order()?.orderPositions?.some(
      (p) => p.status === OrderPositionStatusConstants.PositionStatusIdDefault,
    );
  }

  select(orderPositionId: string, selected: boolean): void {
    if (selected) {
      this.selection.add(orderPositionId);
    } else {
      this.selection.delete(orderPositionId);
    }
  }

  claim(): Observable<unknown> {
    const request = new ClaimOrderModel(this.order().orderGroupId);
    request.claimOrderPositions = [...this.selection].map((id) => new ClaimOrderPositionModel(id));

    return this.callApiService
      .callWithToasts$(
        this.orderService.claimOrderPositions(request),
        $localize`Stornierung wurde angefordert`,
        $localize`Beim Stornieren der Positionen ist ein Fehler aufgetreten`,
      )
      .pipe(
        tap(() => {
          // The backend refuses a position that is no longer simply bought, so the same
          // selection must not be sendable a second time.
          this.selection.clear();
          this.claimed.emit();
        }),
      );
  }
}
